import { FunctionComponent, useContext } from 'react';

import { useMutation } from '@apollo/client';
import { Button, SeasonPassIcon, Separator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { LocalizationContext } from 'localization';
import { GET_USER_BY_UUID, SEARCH_CLIENTS } from 'schemas';
import { openConfirmDialog } from 'services/Confirmation';
import { IPausableInfo } from 'types';
import { transformTime } from 'utils';

import { CollapsibleItem } from '../CollapsibleItem';
import s from './PausableTariff.module.scss';
import { STOP_PAUSABLE_TARIFF } from './schema';

interface IPausableTariffProps {
  nickname?: string;
  pausableInfo?: IPausableInfo;
}

export const PausableTariff: FunctionComponent<IPausableTariffProps> = ({
  nickname,
  pausableInfo,
}) => {
  const { t } = useContext(LocalizationContext);

  const { duration = 0, elapsed = 0, tariff, expires_at } = pausableInfo ?? {};

  const restTime = transformTime(duration - elapsed, t);
  const expiresAt = dayjs(expires_at);

  const [stopClientSession, { loading }] = useMutation(STOP_PAUSABLE_TARIFF, {
    refetchQueries: [GET_USER_BY_UUID, SEARCH_CLIENTS],
  });

  return (
    <CollapsibleItem
      title={t('client.pausable.title')}
      Icon={SeasonPassIcon}
      color={pausableInfo ? 'orange' : undefined}
    >
      {pausableInfo ? (
        <div className={s.container}>
          <div className={s.row}>
            <span className={s.title}>{t('title')}</span>
            {tariff?.title}
          </div>
          <div className={s.row}>
            <span className={s.title}>{t('client.pausable.rest')}</span>
            {restTime?.title}
          </div>
          <Separator />
          <div className={s.row}>
            <span className={s.title}>{t('client.pausable.ends')}</span>
            {expiresAt.format('DD MMM YYYY HH:mm:ss')}
          </div>
          <Button
            variant="outline"
            disabled={loading}
            onClick={() =>
              openConfirmDialog(t, 'remove', {
                title: t('client.pausable.finish'),
                message: t(
                  'client.pausable.finish.description',
                  nickname ?? ''
                ),
                acceptTitle: t('client.pausable.finish.accept'),
                declineTitle: t('client.pausable.finish.decline'),
              }).then(({ status }) => {
                if (status === 'ACCEPTED') {
                  return stopClientSession({
                    variables: { id: pausableInfo?.id, ignorePause: true },
                  });
                }
              })
            }
            fullWidth
          >
            {t('client.pausable.finish')}
          </Button>
        </div>
      ) : (
        t('client.pausable.placeholder')
      )}
    </CollapsibleItem>
  );
};
